import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Fade,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { getAllNotices } from "../../../redux/noticeRelated/noticeHandle";
import { deleteUser } from "../../../redux/userRelated/userHandle";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";

const PRIORITY_MAP = [
    { value: 0, label: "Normal", color: "default" },
    { value: 1, label: "Medium", color: "info" },
    { value: 2, label: "High", color: "warning" },
    { value: 3, label: "Urgent", color: "error" },
];

const AUDIENCE_META = {
    all: { label: "Everyone", Icon: GroupsOutlinedIcon, color: "primary" },
    teacher: { label: "Teachers", Icon: SchoolOutlinedIcon, color: "secondary" },
    student: { label: "Students", Icon: PeopleOutlineIcon, color: "success" },
};

const headCells = [
    { id: "pin", label: "", width: "52px" },
    { id: "priority", label: "Priority", width: "110px" },
    { id: "audience", label: "Audience", width: "130px" },
    { id: "title", label: "Title", width: "22%" },
    { id: "details", label: "Details", width: "28%" },
    { id: "date", label: "Date", width: "110px" },
    { id: "actions", label: "", align: "right", width: "56px" },
];

function TableSkeleton() {
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headCells.map((c) => (
                            <TableCell key={c.id} sx={{ fontWeight: 600 }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <TableRow key={i}>
                            <TableCell colSpan={7}>
                                <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function priorityChip(priority) {
    const pr = priority ?? 0;
    const meta = PRIORITY_MAP.find((p) => p.value === pr) || PRIORITY_MAP[0];
    return <Chip size="small" label={meta.label} color={meta.color} variant={pr === 0 ? "outlined" : "filled"} />;
}

function audienceCell(audienceKey) {
    const aud = String(audienceKey || "all").toLowerCase();
    const meta = AUDIENCE_META[aud] || AUDIENCE_META.all;
    const Icon = meta.Icon;
    return (
        <Chip size="small" icon={<Icon sx={{ "&&": { fontSize: 16 } }} />} label={meta.label} color={meta.color} variant="outlined" />
    );
}

const ShowNotices = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { noticesList, loading, error, response } = useSelector((state) => state.notice);
    const { currentUser } = useSelector((state) => state.user);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const refresh = useCallback(() => {
        if (currentUser?._id) dispatch(getAllNotices(currentUser._id, "Notice"));
    }, [currentUser?._id, dispatch]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const rows = useMemo(() => {
        if (!Array.isArray(noticesList) || noticesList.length === 0) return [];
        return noticesList.map((notice) => {
            const date = new Date(notice.date);
            const dateString = date.toString() !== "Invalid Date" ? date.toLocaleDateString() : "—";
            return {
                id: notice._id,
                pinned: Boolean(notice.pinned),
                priority: notice.priority ?? 0,
                audience: String(notice.targetAudience || "all").toLowerCase(),
                title: notice.title || "",
                details: notice.details || "",
                dateLabel: dateString,
            };
        });
    }, [noticesList]);

    const [deleteDialog, setDeleteDialog] = useState(null);

    const closeDeleteDialog = () => setDeleteDialog(null);

    const openDeleteNoticeDialog = (row) => {
        setDeleteDialog({
            title: `Delete notice: ${row.title}`,
            description: "This will permanently remove this notice from teacher and student portals.",
            confirmWord: row.title,
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(row.id, "Notice"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage("Could not delete the notice.");
                setShowPopup(true);
            },
        });
    };

    const openDeleteAllNoticesDialog = () => {
        setDeleteDialog({
            title: "Delete all notices",
            description: "This will permanently delete every notice for this school. This cannot be undone.",
            confirmWord: "DELETE ALL NOTICES",
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(currentUser._id, "Notices"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage("Could not delete all notices.");
                setShowPopup(true);
            },
        });
    };

    const handleConfirmDelete = async () => {
        if (deleteDialog?.onConfirm) {
            await deleteDialog.onConfirm();
        }
        closeDeleteDialog();
    };

    const hasRows = rows.length > 0;
    const showEmpty = !loading && !error && !hasRows;
    const showTable = !loading && !error && hasRows;
    const paginated = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.info.main, 0.06)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
                pb: 6,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.info.dark} 0%, ${t.palette.info.main} 50%, ${alpha(
                                        t.palette.info.light,
                                        0.92
                                    )} 100%)`,
                                boxShadow: (t) => `0 12px 40px ${alpha(t.palette.info.main, 0.28)}`,
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: alpha("#fff", 0.15),
                                        }}
                                    >
                                        <CampaignOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                            Communications
                                        </Typography>
                                        <Typography variant="h4" component="h1" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                            Notices
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 560, mt: 0.5 }}>
                                            Pin urgent items, target teachers or guardians, and trigger email when SMTP is configured on
                                            the server.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<NoteAddIcon />}
                                        onClick={() => navigate("/Admin/addnotice")}
                                        sx={{
                                            bgcolor: "common.white",
                                            color: "info.dark",
                                            fontWeight: 700,
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                                boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.12)}`,
                                            },
                                        }}
                                    >
                                        New notice
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        color="inherit"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={openDeleteAllNoticesDialog}
                                        sx={{
                                            borderColor: alpha("#fff", 0.55),
                                            color: "common.white",
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                bgcolor: alpha("#fff", 0.08),
                                            },
                                        }}
                                    >
                                        Delete all
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load notices."}
                            </Alert>
                        )}

                        {!error && (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                {loading && (
                                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <TableSkeleton />
                                    </Box>
                                )}

                                {showEmpty && (
                                    <Box sx={{ py: { xs: 6, sm: 8 }, px: 2, textAlign: "center" }}>
                                        <Typography variant="h6" gutterBottom fontWeight={700}>
                                            No notices yet
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: "auto", mb: 3 }}>
                                            {typeof response === "string"
                                                ? response
                                                : "Publish your first announcement with audience targeting and optional email delivery."}
                                        </Typography>
                                        <Button variant="contained" size="large" onClick={() => navigate("/Admin/addnotice")}>
                                            Create notice
                                        </Button>
                                    </Box>
                                )}

                                {showTable && (
                                    <>
                                        <TableContainer>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {headCells.map((c) => (
                                                            <TableCell
                                                                key={c.id}
                                                                align={c.align}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    bgcolor: (t) => alpha(t.palette.info.main, 0.08),
                                                                    whiteSpace: "nowrap",
                                                                    width: c.width,
                                                                }}
                                                            >
                                                                {c.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginated.map((row) => (
                                                        <TableRow
                                                            key={row.id}
                                                            hover
                                                            sx={{
                                                                bgcolor: row.pinned
                                                                    ? (t) => alpha(t.palette.primary.main, 0.04)
                                                                    : "transparent",
                                                                "&:last-child td": { borderBottom: 0 },
                                                            }}
                                                        >
                                                            <TableCell>
                                                                {row.pinned ? (
                                                                    <Tooltip title="Pinned to top">
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                width: 32,
                                                                                height: 32,
                                                                                borderRadius: 1,
                                                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                                                                color: "primary.main",
                                                                            }}
                                                                        >
                                                                            <PushPinOutlinedIcon fontSize="small" />
                                                                        </Box>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Typography variant="body2" color="text.disabled">
                                                                        —
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{priorityChip(row.priority)}</TableCell>
                                                            <TableCell>{audienceCell(row.audience)}</TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={700} noWrap title={row.title}>
                                                                    {row.title}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 0 }}>
                                                                <Typography variant="body2" color="text.secondary" noWrap title={row.details}>
                                                                    {row.details}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {row.dateLabel}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip title="Delete notice">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => openDeleteNoticeDialog(row)}
                                                                        sx={{
                                                                            color: "error.main",
                                                                            bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                                                                            "&:hover": {
                                                                                bgcolor: (t) => alpha(t.palette.error.main, 0.16),
                                                                            },
                                                                        }}
                                                                    >
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            component="div"
                                            count={rows.length}
                                            page={page}
                                            onPageChange={(_, p) => setPage(p)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </Fade>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
            />
        </Box>
    );
};

export default ShowNotices;
